import React from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { AccessControlGroupResponse, SiteResponse, SiteSystemType } from "api/ApiClient";
import { BASE_API_URL } from "store/Me";
import { apiClientFactory } from "helpers";
import { ArrowLeft } from "lucide-react";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import GroupDetails, {
  AdminAccessControlGroup,
  formatGroupType,
} from "./components/GroupDetails";
import GroupLocks from "./GroupLocks";
import GroupGuests from "./GroupGuests";
import GroupSchedules from "./GroupSchedules";

function getCloudManagedSystemType(
  group: AdminAccessControlGroup,
  scopedSite?: SiteResponse,
): SiteSystemType.Brivo | SiteSystemType.Openpath | undefined {
  if (!group.systemGroupId) {
    return undefined;
  }

  const systemType = scopedSite?.account?.systemType;
  if (systemType === SiteSystemType.Brivo || systemType === SiteSystemType.Openpath) {
    return systemType;
  }

  return undefined;
}

function GroupNavigation({
  groupId,
  hasSchlageLock,
}: {
  groupId: string;
  hasSchlageLock: boolean;
}) {
  const links = [
    { to: "locks", label: "Locks" },
    { to: "guests", label: "Guests" },
    { to: "schedules", label: "Schedules", disabled: !hasSchlageLock },
  ];
  const basePath = `/admin/accessControlGroups/${groupId}`;
  const location = useLocation();
  const navigate = useNavigate();

  const activeTo =
    links.find(({ to }) => {
      const path = `${basePath}/${to}`;
      return (
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );
    })?.to ?? links[0].to;

  return (
    <Tabs
      value={activeTo}
      onValueChange={(value) => navigate(`${basePath}/${value}`)}
    >
      <TabsList className="justify-start w-full overflow-x-auto overflow-y-hidden">
        {links.map(({ to, label, disabled }) => (
          <TabsTrigger
            key={to}
            value={to}
            disabled={disabled}
            className="shrink-0 data-[state=active]:bg-slate-200!"
          >
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

export default function AdminGroup() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const {
    data: group,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["acg", "detail", groupId],
    queryFn: async () => {
      const response = await axios.get<AdminAccessControlGroup>(
        `${BASE_API_URL}/api/AccessControlGroups/${groupId}`,
      );
      return response.data;
    },
    enabled: !!groupId,
  });

  const { data: sites } = useQuery({
    queryKey: ["acg", "detail", "sites", groupId],
    queryFn: async () => {
      if (!group?.tenants?.length) {
        return [] as SiteResponse[];
      }

      const externalIds = ((group.tenants as any[]) || [])
        .filter((tenant) => tenant?.tenant?.externalId)
        .map((tenant) => tenant.tenant.externalId);

      const siteSets = await Promise.all(
        externalIds.map((externalId) =>
          apiClientFactory().getSites(undefined, undefined, undefined, externalId),
        ),
      );

      return siteSets.flat();
    },
    enabled: !!group,
  });

  const { data: groupLocks = [] } = useQuery({
    queryKey: ["acg", "locks", "for-schedules", groupId],
    queryFn: () => apiClientFactory().getLocksForAccessControlGroup(groupId!, 0, 2000, undefined),
    enabled: !!groupId,
  });

  const scopedSite = React.useMemo(() => {
    if (!group?.siteId) {
      return undefined;
    }

    return (sites || []).find((site) => site.id === group.siteId);
  }, [group?.siteId, sites]);

  const cloudManagedSystemType = React.useMemo(
    () => (group ? getCloudManagedSystemType(group, scopedSite) : undefined),
    [group, scopedSite],
  );

  const hasSchlageLock = React.useMemo(
    () =>
      groupLocks.some(
        (groupLock) =>
          groupLock.lock?.site?.account?.systemType === SiteSystemType.SchlageEngage,
      ),
    [groupLocks],
  );

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiClientFactory().deleteAccessControlGroup(groupId!);
    },
    onSuccess: () => {
      setDeleteError(null);
      setConfirmOpen(false);
      navigate("/admin/accessControlGroups", { replace: true });
    },
    onError: (e: any) => {
      setDeleteError(e?.response?.data?.message || e?.message || "Unable to delete group.");
    },
  });

  if (isError) {
    const status = (error as any)?.response?.status;
    if (status === 404) {
      return <Navigate to="/admin/accessControlGroups" replace />;
    }
    return (
      <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
        <Link
          to="/admin/accessControlGroups"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Access Control Groups
        </Link>
        <Alert variant="destructive">
          Unable to load group. Please try again.
        </Alert>
      </main>
    );
  }

  if (isLoading || !group) {
    return (
      <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
        <Link
          to="/admin/accessControlGroups"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Access Control Groups
        </Link>
        <div className="flex items-center justify-center text-sm bg-white border rounded-lg shadow-sm min-h-80 border-slate-200 text-slate-500">
          Loading group...
        </div>
      </main>
    );
  }

  return (
    <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
      <header className="pb-5 border-b border-slate-200">
        <Link
          to="/admin/accessControlGroups"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Access Control Groups
        </Link>
      </header>

      {cloudManagedSystemType ? (
        <Alert variant="warning">
          Cloud-managed group: this group is managed by{" "}
          {cloudManagedSystemType === SiteSystemType.Openpath ? "OpenPath" : "Brivo"}. The
          cloud system is the source of truth for membership.
        </Alert>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(19rem,24rem)_minmax(0,1fr)]">
        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <div className="px-1">
            <h1 className="text-2xl font-semibold text-slate-900">
              {group.name ?? "Access Control Group"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {formatGroupType(group.type)}
            </p>
          </div>
          <GroupDetails
            group={group}
            siteName={scopedSite?.name}
            onUpdated={async () => {
              await queryClient.invalidateQueries({ queryKey: ["acg", "detail", groupId] });
            }}
            onDeleteClick={() => setConfirmOpen(true)}
          />
        </aside>

        <section className="min-w-0">
          <GroupNavigation groupId={groupId!} hasSchlageLock={hasSchlageLock} />
          <div className="pt-6">
            <Routes>
              <Route index element={<Navigate to="locks" replace />} />
              <Route
                path="locks"
                element={
                  <GroupLocks
                    groupId={groupId!}
                    groupSiteId={group.siteId}
                    cloudManagedSystemType={cloudManagedSystemType}
                  />
                }
              />
              <Route path="guests" element={<GroupGuests groupId={groupId!} />} />
              <Route
                path="schedules"
                element={
                  hasSchlageLock ? (
                    <GroupSchedules groupId={groupId!} group={group as AccessControlGroupResponse} />
                  ) : (
                    <Alert variant="info">
                      Schedules are only available when the group has at least one Schlage
                      lock.
                    </Alert>
                  )
                }
              />
            </Routes>
          </div>
        </section>
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !deleteMutation.isPending) {
            setConfirmOpen(false);
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="sm:max-w-xs"
          onEscapeKeyDown={(event) => {
            if (deleteMutation.isPending) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (deleteMutation.isPending) {
              event.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Delete Access Control Group</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <strong>{group.name || group.id}</strong>? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError ? <Alert variant="destructive">{deleteError}</Alert> : null}
          <DialogFooter>
            <Button
              disabled={deleteMutation.isPending}
              onClick={() => setConfirmOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              type="button"
              variant="destructive"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

